import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const InteractiveWorldMap = () => {
  const [selectedRegion, setSelectedRegion] = useState('asia');

  const regions = [
    {
      id: 'asia',
      name: 'Asia Pacific',
      suppliers: 1240,
      tradeVolume: '₹425 Cr',
      countries: ['India', 'China', 'Japan', 'South Korea', 'Singapore', 'Thailand'],
      icon: 'MapPin',
      color: 'bg-primary'
    },
    {
      id: 'europe',
      name: 'Europe',
      suppliers: 680,
      tradeVolume: '₹285 Cr',
      countries: ['Germany', 'UK', 'France', 'Italy', 'Netherlands', 'Spain'],
      icon: 'Building2',
      color: 'bg-secondary'
    },
    {
      id: 'americas',
      name: 'Americas',
      suppliers: 420,
      tradeVolume: '₹95 Cr',
      countries: ['USA', 'Canada', 'Mexico', 'Brazil', 'Argentina', 'Chile'],
      icon: 'Globe',
      color: 'bg-accent'
    },
    {
      id: 'mena',
      name: 'Middle East & Africa',
      suppliers: 160,
      tradeVolume: '₹45 Cr',
      countries: ['UAE', 'Saudi Arabia', 'Egypt', 'South Africa', 'Kenya', 'Nigeria'],
      icon: 'Compass',
      color: 'bg-success'
    }
  ];

  const activeRegion = regions?.find(r => r?.id === selectedRegion);

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm md:text-base text-primary mb-4">
            <Icon name="Globe2" size={20} />
            <span>Global Network Visualization</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Our Worldwide Trade Network
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Explore our extensive supplier network spanning 45+ countries across four major regions
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2">
            <div className="bg-card rounded-2xl p-6 md:p-8 shadow-lg border border-border">
              <div className="relative w-full aspect-video bg-muted rounded-xl overflow-hidden mb-6">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title="NCHI Global Trade Network Map"
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps?q=20.5937,78.9629&z=2&output=embed"
                  className="border-0"
                ></iframe>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {regions?.map((region) => (
                  <button
                    key={region?.id}
                    onClick={() => setSelectedRegion(region?.id)}
                    className={`p-3 md:p-4 rounded-xl border-2 transition-all duration-200 ${
                      selectedRegion === region?.id
                        ? `${region?.color} text-white border-transparent shadow-lg scale-105`
                        : 'bg-card text-foreground border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon name={region?.icon} size={24} className="mx-auto mb-2" />
                    <div className="text-xs md:text-sm font-semibold">{region?.name}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4 md:space-y-6">
            <div className="bg-card rounded-2xl p-6 shadow-lg border border-border">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 ${activeRegion?.color} rounded-xl flex items-center justify-center`}>
                  <Icon name={activeRegion?.icon} size={24} color="white" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-foreground">{activeRegion?.name}</h3>
                  <p className="text-sm text-muted-foreground">Regional Overview</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <Icon name="Users" size={18} color="var(--color-primary)" />
                    <span className="text-sm text-muted-foreground">Suppliers</span>
                  </div>
                  <span className="text-base md:text-lg font-bold text-foreground">{activeRegion?.suppliers}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <Icon name="TrendingUp" size={18} color="var(--color-success)" />
                    <span className="text-sm text-muted-foreground">Trade Volume</span>
                  </div>
                  <span className="text-base md:text-lg font-bold text-foreground">{activeRegion?.tradeVolume}</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <Icon name="MapPin" size={18} color="var(--color-accent)" />
                    <span className="text-sm text-muted-foreground">Countries</span>
                  </div>
                  <span className="text-base md:text-lg font-bold text-foreground">{activeRegion?.countries?.length}</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-border">
                <h4 className="text-sm font-semibold text-foreground mb-3">Key Markets</h4>
                <div className="flex flex-wrap gap-2">
                  {activeRegion?.countries?.map((country) => (
                    <span
                      key={country}
                      className="px-3 py-1 bg-muted text-xs md:text-sm text-foreground rounded-full"
                    >
                      {country}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary to-secondary rounded-2xl p-6 text-white shadow-lg">
              <Icon name="Award" size={32} className="mb-3" />
              <h4 className="text-lg font-bold mb-2">Verified Network</h4>
              <p className="text-sm text-white/90 mb-4">
                All suppliers undergo rigorous verification and compliance checks
              </p>
              <Button
                variant="default"
                size="sm"
                iconName="Shield"
                iconPosition="left"
                className="bg-white text-primary hover:bg-white/90"
              >
                View Certification
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveWorldMap;